// src/services/token-manager.ts
import { ACS_LIGHT_API_BASE_PATH} from '../config';
import { indexedDBStorage } from './storage/indexedDB';
import { setAuthDiag, logWarning } from '../utils/errorLogger';

/**
 * 起動直後の「トークン受け渡し窓」対策の待ち時間。
 * Flutter の IndexedDB 注入直後や、別の並列呼び出しによるリフレッシュ直後は、
 * token がまだ読めないことがある。ここで即 null 判定にすると /menu の並列API群が
 * 一斉に「認証トークンがありません」で失敗するため、短時間だけ token の出現を待つ。
 */
const TOKEN_WAIT_TOTAL_MS = 2000;
const TOKEN_WAIT_INTERVAL_MS = 200;

/** 指定キーが現れるまで（または期限切れまで）ポーリングして待つ。 */
async function waitForStorageItem(
  key: string,
  totalMs: number,
  intervalMs: number,
): Promise<string | null> {
  const deadline = Date.now() + totalMs;
  let value = await indexedDBStorage.getItem(key);
  while (!value && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    value = await indexedDBStorage.getItem(key);
  }
  return value;
}

/**
 * トークン設定
 */
const TOKEN_CONFIG = {
  LIFETIME: 60 * 60 * 1000,      // 1時間（ミリ秒）
  SAFETY_MARGIN: 5 * 60 * 1000,  // 5分のマージン（55分経過したらリフレッシュ）
};

// ---------------------------------------------------------------------------
// 同時実行セマフォ（コールドスタートの接続ストーム対策）
// ---------------------------------------------------------------------------
// 調査結論：/menu 初回表示でゲート開放時に 7〜15 本の認証付きAPIが同一msで一斉発火し、
// 未確立のTLS接続に殺到 → HTTP/2 で多重化された全リクエストが接続確立ごと道連れで
// 失敗する（Load failed / Failed to fetch のバースト）。そこで同時実行を絞り、
// 1本目に接続を確立させてから残りを確立済み接続へ相乗りさせる。
const MAX_CONCURRENT = 3;
// 万一 release 漏れ等が起きてもキューが永久に詰まらないための保険。上限を超えても
// この時間で強制的に通す（＝過剰許可を許してでもデッドロックは絶対に避ける）。
const SEMAPHORE_MAX_WAIT_MS = 10000;
// 同時実行制限を「コールドスタートのストーム窓」だけに限定する時間。
// ストームは起動直後のTLS未確立に多数が殺到する現象なので、その窓の間だけ絞る。
// 窓を過ぎた定常運用（一覧画面の並列取得など）まで永続的に絞ると体感が遅くなるため、
// 窓外では制限しない（HTTP/2 の多重化に任せる）。窓はゲート開放＝bootstrap開始起点。
const STORM_WINDOW_MS = 12000;

let activeSlots = 0;
const slotQueue: Array<() => void> = [];

// いまコールドスタートのストーム窓の中か（ゲート開放から STORM_WINDOW_MS 以内か）。
// 窓外では acquireSlot は即時許可し、同時実行を制限しない。
function inStormWindow(): boolean {
  return bootstrapStarted && Date.now() - bootstrapStartAt <= STORM_WINDOW_MS;
}

function releaseSlot(): void {
  activeSlots = Math.max(0, activeSlots - 1);
  const next = slotQueue.shift();
  if (next) next();
}

/** 実行スロットを1つ確保する。待ち時間(ms)と解放関数を返す。 */
async function acquireSlot(): Promise<{ waitedMs: number; release: () => void }> {
  // ストーム窓の外（定常運用）では制限しない。カウントにも一切触れないので、
  // 窓の内外をまたいでも acquire/release の対応が崩れない（no-op release）。
  if (!inStormWindow()) {
    return { waitedMs: 0, release: () => {} };
  }

  const start = Date.now();
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    releaseSlot();
  };

  if (activeSlots < MAX_CONCURRENT) {
    activeSlots++;
    return { waitedMs: 0, release };
  }

  await new Promise<void>((resolve) => {
    let done = false;
    const grant = () => {
      if (done) return;
      done = true;
      activeSlots++;
      resolve();
    };
    slotQueue.push(grant);
    // 保険：一定時間で必ず通す（キューからは取り除く）。過剰許可より詰まりを避ける。
    setTimeout(() => {
      if (done) return;
      const idx = slotQueue.indexOf(grant);
      if (idx >= 0) slotQueue.splice(idx, 1);
      done = true;
      activeSlots++;
      resolve();
    }, SEMAPHORE_MAX_WAIT_MS);
  });

  return { waitedMs: Date.now() - start, release };
}

/**
 * 認証ブートストラップ・ゲート。
 *
 * 起動直後、トークン注入（Flutter）と権限確定（getLoginUserInfo）が済む前に、
 * /menu の並列データAPIが一斉発火して「認証トークンがありません」/ Load failed の
 * バーストを起こす（cache だけでは cold-start の初回バーストは止まらない）。
 * そこで、認証付きリクエストをブートストラップ完了までこのゲートで待たせる。
 *
 * - AuthProvider が「プローブ(getLoginUserInfo)実成功」/「未認証」/「login」時に
 *   openAuthGate(reason) を呼んで解放する（＝接続が生きた確証を得てから開ける）。
 * - ブートストラップ自身（getLoginUserInfo・refresh・login）は待つと自己デッドロック
 *   するため、呼び出し側が skipGate:true を渡して bypass する。
 * - 万一 openAuthGate() が来なくても、AUTH_GATE_MAX_WAIT_MS で必ず解放する（ハング防止）。
 */
const AUTH_GATE_MAX_WAIT_MS = 8000;
let authGateOpened = false;
let authGateResolve: (() => void) | null = null;
let authGatePromise: Promise<void> | null = new Promise((resolve) => {
  authGateResolve = resolve;
});
let gateOpenReason: string | null = null;
let gateFallbackTimer: ReturnType<typeof setTimeout> | undefined;

/** ゲート開放理由（診断用）。'probe-success' / 'timeout' / 'unauth' / 'login' 等。 */
export function getGateOpenReason(): string | null {
  return gateOpenReason;
}

/** ブートストラップ完了。以降ゲートは待たない（冪等）。理由を診断へ記録する。 */
export function openAuthGate(reason: string = 'unknown'): void {
  if (authGateOpened) return;
  authGateOpened = true;
  gateOpenReason = reason;
  setAuthDiag({ gateOpenReason: reason });
  bootstrapStart();
  if (gateFallbackTimer) {
    clearTimeout(gateFallbackTimer);
    gateFallbackTimer = undefined;
  }
  authGateResolve?.();
  authGateResolve = null;
  authGatePromise = null;
}

/**
 * ゲートを閉じ直す（ログアウト時）。次回ログイン/復元で改めて開かせる。
 * これをしないと、同一JSコンテキストでログアウト→再ログインした際にゲートが
 * 開いたままとなり、再ログインのブートストラップを待たずにAPIが発火しうる。
 * （実害は login() が同期でトークンを確立するため小さいが、契約を正しく保つ）
 * bootstrap 集計状態は初回計測のまま据え置き、再ログインで storm 窓を再開しない。
 */
export function resetAuthGate(): void {
  authGateOpened = false;
  gateOpenReason = null;
  if (gateFallbackTimer) {
    clearTimeout(gateFallbackTimer);
    gateFallbackTimer = undefined;
  }
  authGatePromise = new Promise((resolve) => {
    authGateResolve = resolve;
  });
}

/** ゲートが開くまで（または保険タイムアウトまで）待つ。 */
async function waitForAuthGate(): Promise<void> {
  if (authGateOpened) return;
  const p = authGatePromise;
  if (!p) return;
  // 最初の待ち手が来たら保険タイマーを起動（openAuthGate が来なくても必ず開く）。
  if (!gateFallbackTimer) {
    gateFallbackTimer = setTimeout(() => openAuthGate('timeout'), AUTH_GATE_MAX_WAIT_MS);
  }
  await p;
}

// ---------------------------------------------------------------------------
// ブートストラップ要約（効果測定）
// ---------------------------------------------------------------------------
// エラーログは「失敗」しか残らないため、修正で「失敗→silent回復」に転じたことを
// 積極的に確認できない。そこでゲート開放後の一定窓で結果を集計し、問題があった
// セッションだけ要約を1件送る。健全セッションは送らない（ノイズ抑制）。
const BOOTSTRAP_WINDOW_MS = 12000;
let bootstrapStarted = false;
let bootstrapStartAt = 0;
const bootstrap = {
  apiTotal: 0,
  firstAttemptFail: 0,
  recoveredByRetry: 0,
  finalFail: 0,
  maxConcurWaitedMs: 0,
};

function bootstrapStart(): void {
  if (bootstrapStarted) return;
  bootstrapStarted = true;
  bootstrapStartAt = Date.now();
  setTimeout(bootstrapFlush, BOOTSTRAP_WINDOW_MS);
}

function bootstrapRecord(evt: {
  firstAttemptFailed: boolean;
  recovered: boolean;
  finalFailed: boolean;
  concurWaitedMs: number;
}): void {
  if (!bootstrapStarted) return;
  if (Date.now() - bootstrapStartAt > BOOTSTRAP_WINDOW_MS) return;
  bootstrap.apiTotal++;
  if (evt.firstAttemptFailed) bootstrap.firstAttemptFail++;
  if (evt.recovered) bootstrap.recoveredByRetry++;
  if (evt.finalFailed) bootstrap.finalFail++;
  if (evt.concurWaitedMs > bootstrap.maxConcurWaitedMs) {
    bootstrap.maxConcurWaitedMs = evt.concurWaitedMs;
  }
}

function bootstrapFlush(): void {
  // 問題があったセッションだけ記録する（初回失敗が有った／保険解放だった）。
  const interesting =
    bootstrap.firstAttemptFail > 0 ||
    bootstrap.finalFail > 0 ||
    gateOpenReason === 'timeout';
  if (interesting) {
    logWarning('diag.menuBootstrap', 'メニュー起動時の認証付きAPIバースト集計', {
      gateOpenReason,
      bootstrapMs: bootstrapStartAt ? Date.now() - bootstrapStartAt : null,
      apiTotal: bootstrap.apiTotal,
      firstAttemptFail: bootstrap.firstAttemptFail,
      recoveredByRetry: bootstrap.recoveredByRetry,
      finalFail: bootstrap.finalFail,
      maxConcurWaitedMs: bootstrap.maxConcurWaitedMs,
    });
  }
}

/**
 * Flutter(WebView)へ新トークンを同期する。
 *
 * 単回使用リフレッシュトークンは Web 側でリフレッシュするたびにローテーションする。
 * Flutter は初回ログイン時のトークンしか保持しておらず、コールド起動時にその
 * 「既に消費済み」のトークンを IndexedDB へ注入してしまうと、起動直後のリフレッシュが
 * 必ず失敗する（＝再ログインするまで直らない）。これを防ぐため、Web 側でリフレッシュ
 * するたびに最新トークンを Flutter へ返し、ネイティブ保存と同期させる。
 */
// Flutter からの同期 ack を受け取るフック（Flutter が prefs 保存完了後に呼ぶ）。
let tokenSyncSeq = 0;
const pendingTokenSyncs = new Map<number, () => void>();
function ensureTokenSyncAckHook(): void {
  const w = window as any;
  if (w.__flutterTokenSyncAck) return;
  w.__flutterTokenSyncAck = (id: number) => {
    const resolve = pendingTokenSyncs.get(Number(id));
    if (resolve) {
      pendingTokenSyncs.delete(Number(id));
      resolve();
    }
  };
}

function syncTokensToFlutter(token: string, refreshToken: string): void {
  try {
    const bridge = (window as any).FlutterTokenSync;
    if (!bridge || typeof bridge.postMessage !== 'function') return;
    // 単回使用RTのローテーション desync を防ぐため、fire-and-forget をやめて
    // Flutter の ack を待ち、未達なら短間隔で数回再送する（同一トークンの再送は冪等）。
    // ここで届かないと Flutter prefs が旧（破棄済）RTのまま残り、次回コールド起動で 400 を量産する。
    ensureTokenSyncAckHook();
    const id = ++tokenSyncSeq;
    const MAX_ATTEMPTS = 3;
    const RESEND_MS = 1000;
    let attempts = 0;
    const send = () => {
      attempts++;
      try {
        bridge.postMessage(JSON.stringify({ token, refreshtoken: refreshToken, syncId: id }));
      } catch (_) {
        /* noop */
      }
      const timer = setTimeout(() => {
        if (!pendingTokenSyncs.has(id)) return; // ack 済み
        // 新しい同期（より新しいトークン）に取って代わられたら、旧トークンを再送しない。
        // （再送で破棄済み旧RTを Flutter prefs に上書きして 400 を再発させるのを防ぐ）
        if (id !== tokenSyncSeq) {
          pendingTokenSyncs.delete(id);
          return;
        }
        if (attempts < MAX_ATTEMPTS) {
          send(); // 未達→再送
        } else {
          pendingTokenSyncs.delete(id); // 諦める（次回リフレッシュで再同期）
        }
      }, RESEND_MS);
      pendingTokenSyncs.set(id, () => clearTimeout(timer));
    };
    send();
  } catch (_) {
    // 同期失敗は致命的ではない（次回リフレッシュ時に再同期される）
  }
}

// Flutter への無人再認証要請の多重発火を防ぐデバウンス
let lastReauthRequestAt = 0;
const REAUTH_DEBOUNCE = 10 * 1000; // 10秒

/**
 * Flutter(WebView)へ無人再認証を要請する。
 *
 * Web 側のリフレッシュトークンも失効しており、Web だけでは新トークンを取得できない
 * ケースで呼ぶ。Flutter はネイティブ保存のリフレッシュトークンで再認証を試み、成功
 * すれば新トークンを IndexedDB へ注入して menu を再読込する（ユーザー操作不要）。
 * 失敗した場合のみ Flutter がログイン画面を表示する。
 * @returns Flutter へ要請したら true（ブラウザ単体など Flutter 不在なら false）
 */
function requestFlutterReauth(): boolean {
  try {
    const bridge = (window as any).FlutterReauth;
    if (bridge && typeof bridge.postMessage === 'function') {
      const now = Date.now();
      if (now - lastReauthRequestAt < REAUTH_DEBOUNCE) {
        return true; // 直近で要請済み（Flutter 側で処理中）
      }
      lastReauthRequestAt = now;
      bridge.postMessage('reauth');
      return true;
    }
  } catch (_) {
    // noop
  }
  return false;
}

// 最後の /refresh が「サーバ到達・RT拒否（body statuscode 4xx）」で終わったか。
// 接続断(throw)と区別する。②クリーン回復は "拒否" の時だけ発火させ、一過性の接続失敗で
// セッションを落とす（白画面/ログイン戻りループ）回帰を防ぐ。
let lastRefreshRejected = false;

// ブラウザ（Flutter不在）で Web だけでは回復不能＝refreshトークンが破棄/失効(body 4xx)に
// なった場合のクリーン回復。無効トークンを掃除し、ログイン画面へ SPA 遷移してループを断つ。
// （RTは単回使用確定。拒否された旧RTは保持しても毎回400になるだけ＝破棄して再ログインが正。）
// 接続断(throw)では呼ばない（lastRefreshRejected ガード）。フルリロード(location.href)ではなく
// window.__flutterNavigate(=React Router の SPA 遷移)を優先し、白画面回帰を避ける。
let lastWebReauthAt = 0;
async function requestWebReauth(): Promise<void> {
  if (!lastRefreshRejected) return; // 接続断・未確定ではセッションを落とさない
  const now = Date.now();
  if (now - lastWebReauthAt < REAUTH_DEBOUNCE) return;
  lastWebReauthAt = now;
  try {
    await indexedDBStorage.removeItem('token');
    await indexedDBStorage.removeItem('refreshToken');
    await indexedDBStorage.removeItem('tokenTimestamp');
    await indexedDBStorage.removeItem('webTokenRefreshedAt');
    // フルリロード退避路(location.assign)でも stale なユーザーを復元しないよう、
    // 遷移前に user/companyId も掃除しておく（SPA 経路では __appForceLogout が重複掃除するが無害）。
    await indexedDBStorage.removeItem('user');
    await indexedDBStorage.removeItem('companyId');
  } catch (_) { /* noop */ }
  // React 認証状態をリセットしてから遷移する。これをしないと /login が isAuthenticated=true を
  // 見て即 /menu へ戻し、トークン無しの壊れた殻でループする（LoginPage の既存リダイレクト）。
  // AuthContext が公開するフック。認証状態が false になれば ProtectedRoute が /login へ誘導する。
  try {
    const forceLogout = (window as any).__appForceLogout;
    if (typeof forceLogout === 'function') { forceLogout(); }
  } catch (_) { /* noop */ }
  try {
    const nav = (window as any).__flutterNavigate;
    if (typeof nav === 'function') { nav('/login'); return; }
  } catch (_) { /* noop */ }
  try { window.location.assign('/login'); } catch (_) { /* noop */ }
}

// 進行中のリフレッシュ（single-flight 用）。複数の並列 API 呼び出しが同時に
// リフレッシュして単回使用トークンを潰し合うのを防ぐため、進行中の1本を共有する。
let refreshInFlight: Promise<boolean> | null = null;

/**
 * トークン管理サービス
 *
 * トークンの有効期限を管理し、必要に応じて自動的にリフレッシュする
 */
export const tokenManager = {
  /**
   * 有効なトークンを取得（必要に応じて自動リフレッシュ）
   * @returns 有効なトークン、または null
   */
  async getValidToken(): Promise<string | null> {
    let token = await indexedDBStorage.getItem('token');

    // トークンが読めない場合の回復（起動直後の受け渡し窓対策）。
    // 即 null を返すと /menu 等の並列APIが一斉に失敗＆ログ氾濫するため、次の順で回復を試みる。
    if (!token) {
      const hadRefreshToken = !!(await indexedDBStorage.getItem('refreshToken'));

      // ① 保存済み refreshToken があれば、まずリフレッシュして新トークンを取得（single-flight）。
      if (hadRefreshToken) {
        const refreshed = await this.refreshTokenDirect();
        if (refreshed) {
          token = await indexedDBStorage.getItem('token');
        }
      }

      // ② それでも無ければ、Flutter 注入完了待ちとして短時間だけ token の出現を待つ。
      if (!token) {
        token = await waitForStorageItem('token', TOKEN_WAIT_TOTAL_MS, TOKEN_WAIT_INTERVAL_MS);
      }

      // ③ 待っている間に refreshToken だけ現れたら、それでリフレッシュして取得を試みる。
      if (!token) {
        const rt = await indexedDBStorage.getItem('refreshToken');
        if (rt) {
          const refreshed = await this.refreshTokenDirect();
          if (refreshed) {
            token = await indexedDBStorage.getItem('token');
          }
        }
      }

      // エラー発生時にログへ同梱される補足。お客様端末を見られないため、
      // 「その瞬間 token が読めず（回復可否/refreshToken有無）」を記録しておく。
      setAuthDiag({
        hasToken: !!token,
        hadRefreshToken,
        tokenRecovered: !!token,
        tokenCheckPath:
          typeof window !== 'undefined' ? window.location.pathname : '',
      });
      if (!token) {
        return null;
      }
    }

    const timestamp = await indexedDBStorage.getItem('tokenTimestamp');
    const now = Date.now();

    // タイムスタンプがない、または期限切れ間近（55分経過）
    const shouldRefresh = !timestamp || (now - parseInt(timestamp)) > (TOKEN_CONFIG.LIFETIME - TOKEN_CONFIG.SAFETY_MARGIN);

    if (shouldRefresh) {
      // リフレッシュが必要
      // 循環参照を避けるため、ここでは直接リフレッシュAPIを呼び出す
      const refreshed = await this.refreshTokenDirect();
      if (!refreshed) {
        return null;
      }
    }

    return await indexedDBStorage.getItem('token');
  },

  /**
   * トークンとタイムスタンプを保存
   * @param token アクセストークン
   * @param refreshToken リフレッシュトークン
   */
  async saveTokenWithTimestamp(token: string, refreshToken: string): Promise<void> {
    await indexedDBStorage.setItem('token', token);
    await indexedDBStorage.setItem('refreshToken', refreshToken);
    await indexedDBStorage.setItem('tokenTimestamp', Date.now().toString());
    // エラーlog同梱用スナップショット：トークンは今揃っている
    setAuthDiag({ hasToken: true, hadRefreshToken: true });
    // Flutter 保存と同期（コールド起動時の消費済みトークン注入を防ぐ）
    syncTokensToFlutter(token, refreshToken);
  },

  /**
   * トークンを直接リフレッシュ（循環参照を避けるため）。
   * 複数の並列 API 呼び出しが同時にリフレッシュして単回使用トークンを潰し合うのを
   * 防ぐため、進行中のリフレッシュは1本に集約する（single-flight）。
   * @returns 成功した場合 true
   */
  async refreshTokenDirect(): Promise<boolean> {
    // 既にリフレッシュが進行中なら、その結果を共有する
    if (refreshInFlight) {
      return refreshInFlight;
    }
    // リフレッシュ本体（既存ロジック）。⑤クロスタブロック取得後にのみ呼ぶ。
    const runRefresh = async (): Promise<boolean> => {
      try {
        const refreshToken = await indexedDBStorage.getItem('refreshToken');

        if (!refreshToken) {
          setAuthDiag({ refreshTransport: 'no-refresh-token' });
          // RT未注入の過渡期もあり得るため、web からはセッションを落とさない。
          lastRefreshRejected = false;
          return false;
        }

        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${refreshToken}`,
            'Cache-Control': 'no-cache, no-store',
            'Pragma': 'no-cache'
          },
          body: JSON.stringify({})
        };

        // 無応答で固まらないようタイムアウト付きで実行。
        // リフレッシュは全API呼び出しの前提となる critical path。単回使用トークンの
        // 競合やモバイル回線の遅延で 8s では起動時の唯一のリフレッシュが失敗しやすかったため、
        // 認証回復の成功率を上げる目的で 12s に延長する（失敗時フォールバックが数秒遅れるのみ）。
        const response = await fetchWithTimeout(
          `${ACS_LIGHT_API_BASE_PATH}/api2/authentication/refresh`,
          requestOptions,
          12000,
        );
        const data = await response.json();

        // 診断：/refresh が「HTTP応答を返した(＝サーバー到達)」ことを記録する。
        // これが記録されていれば失敗は認証レベル、後述 catch の 'throw' なら接続レベル。
        setAuthDiag({ refreshTransport: `http:${data?.statuscode ?? response.status}` });

        if (data.statuscode !== 200) {
          // サーバ到達・RT拒否（単回使用で破棄済/失効）。②クリーン回復の対象。
          // 仕様上 400 には message（理由）が付くので診断へ残す（期限切れ/無効の切り分け用）。
          lastRefreshRejected = true;
          setAuthDiag({
            refreshRejectMsg:
              typeof data?.message === 'string' ? data.message.slice(0, 80) : '',
          });
          return false;
        }

        // 新しいトークンとタイムスタンプを保存（Flutter へも同期される）
        lastRefreshRejected = false;
        await this.saveTokenWithTimestamp(data.token, data.refreshtoken);

        return true;
      } catch (error) {
        // 診断：/refresh がHTTP応答に至らず例外で落ちた＝サーバー未到達の接続レベル失敗。
        // （/refresh はどんなトークンでも 200+CORS を返す仕様なので、ここに来る＝接続断）
        setAuthDiag({
          refreshTransport: 'throw',
          refreshErrKind: (error as any)?.name ?? 'unknown',
        });
        // 接続断（サーバ未到達）はセッション維持（白画面/ログイン戻り回帰防止）。
        lastRefreshRejected = false;
        console.error('Token refresh error:', error);
        return false;
      }
    };

    // ⑤ クロスタブ排他: 複数タブが同一の単回使用RTで同時に /refresh すると、1本がRTを
    // ローテーションし、旧RTで走る他タブが statuscode:400 になる。navigator.locks で
    // 直列化し、ロック取得後に「直近で他タブがリフレッシュ済み」なら新トークンを使って
    // 二重リフレッシュ自体を省く。Web Locks 非対応環境では従来どおり実行する。
    const CROSS_TAB_FRESH_MS = 8000;
    const withCrossTabLock = async (): Promise<boolean> => {
      const locks = (navigator as any)?.locks;
      if (!locks || typeof locks.request !== 'function') {
        return runRefresh();
      }
      return locks.request('acs-token-refresh', async (): Promise<boolean> => {
        try {
          const ts = await indexedDBStorage.getItem('tokenTimestamp');
          if (ts && Date.now() - parseInt(ts, 10) < CROSS_TAB_FRESH_MS) {
            const fresh = await indexedDBStorage.getItem('token');
            if (fresh) {
              lastRefreshRejected = false;
              return true; // 他タブが直近にローテーション済み＝新トークンを使う
            }
          }
        } catch (_) {
          /* 再確認失敗時はそのままリフレッシュへ */
        }
        return runRefresh();
      });
    };

    // navigator.locks API 自体が例外（sandboxed/WebView の SecurityError 等）で reject すると、
    // refreshInFlight が rejected promise 化し、getValidToken 等の呼び出し元が false ではなく
    // 例外を投げてしまう（single-flight 共有で全並列APIへ伝播）。必ず boolean に収束させる。
    const guarded = async (): Promise<boolean> => {
      try {
        return await withCrossTabLock();
      } catch (_) {
        // ロックAPIが使えない/例外 → ロック無しで直接リフレッシュ（従来動作にフォールバック）。
        try {
          return await runRefresh();
        } catch (_) {
          lastRefreshRejected = false;
          return false;
        }
      }
    };

    refreshInFlight = guarded().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  },

  /**
   * タイムスタンプをクリア（ログアウト時など）
   */
  async clearTokenTimestamp(): Promise<void> {
    await indexedDBStorage.removeItem('tokenTimestamp');
  }
};

/**
 * API呼び出しラッパー
 * 認証失敗時に自動的にトークンをリフレッシュしてリトライする
 *
 * @param url APIエンドポイントURL
 * @param options fetchオプション
 * @param skipRetry リトライをスキップするか（デフォルト: false）
 * @returns レスポンス
 */
// fetch がサーバ無応答で永久に待ち続け、画面が「読み込み中」のまま固まるのを防ぐため、
// タイムアウト（中断）付きの fetch ラッパー。
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = 15000,
  externalSignal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  // 呼び出し側（UI のキャンセルボタン等）から渡された signal でも中断できるようにする。
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort);
    }
  }
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
  }
}

// AbortError（タイムアウト中断・画面遷移による中断）かどうか
function isAbortError(error: unknown): boolean {
  return (
    error instanceof DOMException && error.name === 'AbortError'
  ) || (error instanceof Error && error.name === 'AbortError');
}

// TypeError（fetch が接続確立レベルで失敗＝Load failed / Failed to fetch）かどうか。
// 調査結論より、コールドスタートのバーストはこの接続レベル失敗が主。認証(401)とは別で、
// 同一トークンのまま少し待って再送すれば回復しうる。
function isConnectionError(error: unknown): boolean {
  return error instanceof TypeError;
}

// 接続失敗時のバックオフ再試行（即時再送は未復旧の同接続に当たり再び全滅するため）。
const CONNECT_RETRY_BACKOFFS_MS = [500, 1500];

export async function fetchWithTokenRefresh(
  url: string,
  options: RequestInit = {},
  skipRetry: boolean = false,
  opts?: { timeoutMs?: number; signal?: AbortSignal; skipGate?: boolean }
): Promise<Response> {
  // 0. 認証ブートストラップが済むまで待つ（cold-start の並列バースト抑止）。
  //    getLoginUserInfo 等のブートストラップ自身は skipGate:true で bypass する。
  if (!opts?.skipGate) {
    await waitForAuthGate();
  }

  // 1. 有効なトークンを取得（必要に応じて自動リフレッシュ）
  let token = await tokenManager.getValidToken();

  if (!token) {
    // トークンが無い or リフレッシュ不能。Flutter 内なら無人再認証を要請する。
    // Flutter 不在（ブラウザ）で、かつ直近 /refresh が「RT拒否(body 4xx)」だった場合のみ
    // クリーン回復（トークン破棄＋/loginへSPA遷移）し、自己回復不能ループを断つ。
    // 接続断(throw)等では requestWebReauth 内の lastRefreshRejected ガードで何もしない。
    const escalated = requestFlutterReauth();
    if (!escalated) { void requestWebReauth(); }
    bootstrapRecord({ firstAttemptFailed: true, recovered: false, finalFailed: true, concurWaitedMs: 0 });
    setAuthDiag({ fetchFailKind: 'no-token', navOnLine: navigator.onLine });
    throw new Error('認証トークンがありません。再度ログインしてください。');
  }

  // 同時実行数を絞りつつ fetch する（接続ストーム対策）。待ち時間は診断に残す。
  let lastConcurWaitedMs = 0;
  const doFetch = async (t: string) => {
    const slot = await acquireSlot();
    lastConcurWaitedMs = slot.waitedMs;
    try {
      return await fetchWithTimeout(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${t}`,
        },
      }, opts?.timeoutMs ?? 15000, opts?.signal);
    } finally {
      slot.release();
    }
  };

  // ゲート開放後の窓では、初回試行の成否・回復可否を集計する（効果測定）。
  const sinceGateOpenMs = bootstrapStartAt ? Date.now() - bootstrapStartAt : null;
  let firstAttemptFailed = false;
  let recovered = false;

  try {
    let response = await doFetch(token);

    // 2. HTTP 401（応答が読める場合）は1回だけリフレッシュしてリトライ
    if (response.status === 401 && !skipRetry) {
      firstAttemptFailed = true;
      const refreshed = await tokenManager.refreshTokenDirect();
      if (refreshed) {
        const newToken = await indexedDBStorage.getItem('token');
        if (newToken) {
          response = await doFetch(newToken);
          recovered = response.status !== 401;
        }
      } else {
        // Web 側リフレッシュ不能 → Flutter へ無人再認証を要請
        requestFlutterReauth();
      }
    }

    bootstrapRecord({
      firstAttemptFailed,
      recovered,
      finalFailed: false,
      concurWaitedMs: lastConcurWaitedMs,
    });
    return response;
  } catch (error) {
    firstAttemptFailed = true;
    // AbortError（タイムアウト中断）。主因は iOS WKWebView がネイティブカメラ表示中に
    // webview をサスペンドし、進行中の接続が切れること（/camera に集中）。この場合は
    // 待ち時間を延ばしても接続断で完了せず、復帰後の「再送」でのみ回復する。
    // 認証とは無関係なのでリフレッシュはせず、同じトークンで1回だけ再試行する。
    // 二重送信が致命的な書き込み系は skipRetry=true が付いており再試行されない
    // （今回対象になるのは getTodoCount 等の読み取り系のみ＝冪等で安全）。
    if (isAbortError(error)) {
      // 外部 signal（UI のキャンセルボタン等）による意図的な中断は、リトライすると
      // キャンセルが無効化されてしまうため再試行しない。タイムアウト（内部 controller
      // による中断で外部 signal は aborted ではない）の場合のみ、従来どおり冪等な
      // 読み取り系を1回だけ再試行する。
      if (!skipRetry && !opts?.signal?.aborted) {
        try {
          const r = await doFetch(token);
          bootstrapRecord({ firstAttemptFailed, recovered: true, finalFailed: false, concurWaitedMs: lastConcurWaitedMs });
          return r;
        } catch (e2) {
          setAuthDiag({ fetchFailKind: 'abort', navOnLine: navigator.onLine, sinceGateOpenMs });
          bootstrapRecord({ firstAttemptFailed, recovered: false, finalFailed: true, concurWaitedMs: lastConcurWaitedMs });
          throw e2;
        }
      }
      setAuthDiag({ fetchFailKind: 'abort', navOnLine: navigator.onLine, sinceGateOpenMs });
      bootstrapRecord({ firstAttemptFailed, recovered: false, finalFailed: true, concurWaitedMs: lastConcurWaitedMs });
      throw error;
    }

    // 本番の認証エラーは CORS で応答が握り潰され TypeError: Load failed 化する
    // （click.acs-l.jp の 401/400 応答に CORS ヘッダが付かないため、ブラウザが応答を
    // 破棄し fetch が reject する）。成功 200 応答は CORS を通過するので、この
    // TypeError は「サーバに拒否された（認証）」か「そもそも接続確立に失敗した（接続断）」
    // のどちらか。まずリフレッシュを試み、成功すれば認証由来として1回再試行する。
    //
    // 二重登録が致命的な書き込み系は skipRetry=true を渡して再試行を抑止する
    // （その場合でもトークンは修復され、次回操作から回復する）。
    const refreshed = await tokenManager.refreshTokenDirect();
    setAuthDiag({
      fetchFailKind: isConnectionError(error) ? 'TypeError' : ((error as any)?.name ?? 'error'),
      navOnLine: navigator.onLine,
      sinceGateOpenMs,
      concurWaitedMs: lastConcurWaitedMs,
    });
    if (refreshed && !skipRetry) {
      const newToken = await indexedDBStorage.getItem('token');
      if (newToken) {
        try {
          const r = await doFetch(newToken);
          recovered = true;
          bootstrapRecord({ firstAttemptFailed, recovered, finalFailed: false, concurWaitedMs: lastConcurWaitedMs });
          return r;
        } catch (_) {
          // リフレッシュしても再び失敗 → 下の接続バックオフへフォールバック
        }
      }
    }

    // リフレッシュが「例外(refreshTransport:'throw')」で落ちた＝接続レベルの失敗。
    // 即時再送は未復旧の同接続に当たり再全滅するため、バックオフしてから同一トークンで
    // 再試行する（冪等な読み取り系のみ。書き込み系 skipRetry は再試行しない）。
    if (!refreshed && !skipRetry && isConnectionError(error)) {
      for (const backoff of CONNECT_RETRY_BACKOFFS_MS) {
        await new Promise((resolve) => setTimeout(resolve, backoff));
        try {
          const r = await doFetch(token);
          recovered = true;
          bootstrapRecord({ firstAttemptFailed, recovered, finalFailed: false, concurWaitedMs: lastConcurWaitedMs });
          return r;
        } catch (e2) {
          if (!isConnectionError(e2)) {
            // 接続系でなくなった（別の恒久エラー）ならバックオフ継続の意味がない
            error = e2;
            break;
          }
          // まだ接続系 → 次のバックオフへ
        }
      }
    }

    if (!refreshed) {
      // Web 側リフレッシュ不能 → Flutter へ無人再認証を要請（成功すれば再注入+再読込）
      requestFlutterReauth();
    }
    bootstrapRecord({ firstAttemptFailed, recovered: false, finalFailed: true, concurWaitedMs: lastConcurWaitedMs });
    throw error;
  }
}
